import React from "react";

import { formatDateOnly } from "../../../../utils/datetime";
import { chfPrecise } from "../../../../components/admin/companyFinance/financeFormat";
import {
  BigValue,
  CardCta,
  CardNeutralState,
  HomeIcon,
  OverviewCard,
  StatusPill,
} from "./overviewUi";

/** Display status → pill tone. Same five keys the backend can derive. */
const STATUS_TONE = {
  active: "emerald",
  reserved: "sky",
  notice_given: "amber",
  ended: "slate",
  cancelled: "rose",
};

const STATUS_LABEL = {
  active: "Aktiv",
  reserved: "Reserviert",
  notice_given: "Gekündigt",
  ended: "Beendet",
  cancelled: "Storniert",
};

/**
 * "Aktuelles Mietverhältnis".
 *
 * MONTHLY RENT SOURCE. `monthly_revenue_equivalent` — the sum of the tenancy's
 * TenancyRevenue rows active on the Swiss business day, computed server-side and
 * batch-attached to the list response (no query per row). NOT
 * `Tenancy.monthly_rent`, which the model marks deprecated with "Do NOT read in
 * new code" and which the revenue decision record replaced.
 *
 * It is a SUM of the recurring lines (rent plus whatever else is booked as
 * recurring), so the label says "Monatlich" rather than "Monatsmiete" — calling
 * a total the base rent would be the kind of quiet mislabel that survives for
 * years. `has_rent_revenue` distinguishes "no revenue rows recorded" from a
 * genuine zero.
 *
 * The status pill uses `display_status`, the backend's date-based derivation —
 * not the raw `status` column, which reads `reserved` for many tenants who have
 * long since moved in.
 */
export default function CurrentTenancyCard({ tenancy, onOpen }) {
  if (!tenancy) {
    return (
      <OverviewCard
        title="Aktuelles Mietverhältnis"
        icon={<HomeIcon />}
        accent="blue"
        testId="overview-tenancy-card"
        footer={
          onOpen ? (
            <CardCta onClick={onOpen} testId="overview-tenancy-cta">
              Mietverhältnisse öffnen
            </CardCta>
          ) : null
        }
      >
        <CardNeutralState
          testId="overview-tenancy-empty"
          text="Zurzeit läuft kein Mietverhältnis. Frühere und geplante Mietverhältnisse stehen im Tab «Mietverhältnisse»."
        />
      </OverviewCard>
    );
  }

  const statusKey = String(tenancy.display_status || "").toLowerCase();
  const objectParts = [
    tenancy.unit_title,
    tenancy.room_name,
    // Only when the room actually records a size — "— m²" is noise.
    tenancy.room_size_m2 != null && Number(tenancy.room_size_m2) > 0
      ? `${Number(tenancy.room_size_m2).toLocaleString("de-CH")} m²`
      : null,
    tenancy.unit_short_unit_id,
  ].filter((p) => p != null && String(p).trim() !== "");

  const from = tenancy.move_in_date ? formatDateOnly(tenancy.move_in_date) : null;
  // `display_end_date`, not the raw move_out_date: a termination REPLACES the
  // contractual end and the backend already resolved which one applies.
  const to = tenancy.display_end_date
    ? formatDateOnly(tenancy.display_end_date)
    : null;

  const hasRevenue = tenancy.has_rent_revenue === true;
  const monthly =
    tenancy.monthly_revenue_equivalent === null ||
    tenancy.monthly_revenue_equivalent === undefined
      ? null
      : chfPrecise(tenancy.monthly_revenue_equivalent);

  return (
    <OverviewCard
      title="Aktuelles Mietverhältnis"
      icon={<HomeIcon />}
      accent="blue"
      testId="overview-tenancy-card"
      action={
        statusKey ? (
          <StatusPill
            tone={STATUS_TONE[statusKey] || "slate"}
            testId="overview-tenancy-status"
          >
            {STATUS_LABEL[statusKey] || statusKey}
          </StatusPill>
        ) : null
      }
      footer={
        onOpen ? (
          <CardCta onClick={onOpen} testId="overview-tenancy-cta">
            Mietverhältnis öffnen
          </CardCta>
        ) : null
      }
    >
      {objectParts.length > 0 ? (
        <p
          className="m-0 text-[14px] font-medium text-card-foreground"
          data-testid="overview-tenancy-object"
        >
          {objectParts.join(" · ")}
        </p>
      ) : null}

      <div className="mt-4">
        <p className="m-0 text-[11.5px] text-muted-foreground">Zeitraum</p>
        <p
          className="m-0 mt-0.5 text-[13.5px] text-card-foreground"
          data-testid="overview-tenancy-period"
        >
          {from ? `${from} – ${to || "offen"}` : "—"}
        </p>
      </div>

      <div className="mt-4">
        <p className="m-0 text-[11.5px] text-muted-foreground">Monatlich</p>
        <BigValue testId="overview-tenancy-rent">
          {hasRevenue && monthly ? monthly : "—"}
        </BigValue>
        {!hasRevenue ? (
          <p className="m-0 mt-1 text-[11.5px] leading-snug text-muted-foreground">
            Keine Mietzins-Positionen erfasst.
          </p>
        ) : null}
      </div>
    </OverviewCard>
  );
}
